import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import * as moment from 'moment';

import { ApiService, HttpMethod } from '../common/api.service';
import { MyCampApp } from '../common/enumerations';
import { UserLoginReportData } from './user-login-report-data';
import { RegisteredUsersReportData } from './registered-users-report-data';
import { ActionReportData } from './action-report-data';
import { NotificationReportData } from './notification-report-data';
import { PhotoUploadReportData } from './photo-upload-report-data';
import { CheckInCheckOutReportData } from './check-in-check-out-report-data';
import UserActionType = MyCampApp.UserActionType;
import { GlobalAdminReportData } from './global-admin-report-data';

@Injectable({
    providedIn: 'root'
})
export class ReportApiService extends ApiService
{
    constructor(protected override http: HttpClient)
    {
        super(http);
    }

    getUserLoginReport(startDate: Date, endDate: Date): Observable<UserLoginReportData>
    {
        return this.apiRequest(this.getUserLoginReportUrl(startDate, endDate), HttpMethod.Get).pipe(map((json: any) => new UserLoginReportData(json)));
    }

    getRegisteredUserReport(startDate: Date, endDate: Date): Observable<RegisteredUsersReportData>
    {
        return this.apiRequest(this.getRegisteredUserReportUrl(startDate, endDate), HttpMethod.Get).pipe(map((json: any) => new RegisteredUsersReportData(json)));
    }

    getUserActionReport(startDate: Date, endDate: Date, actionType: UserActionType, additionalDataId: number): Observable<ActionReportData>
    {
        return this.apiRequest(this.getUserActionReportUrl(startDate, endDate, actionType, additionalDataId), HttpMethod.Get)
            .pipe(map((json: any) => new ActionReportData(json)));
    }

    getNotificationReport(startDate: Date, endDate: Date): Observable<NotificationReportData>
    {
        return this.apiRequest(this.getNotificationReportUrl(startDate, endDate), HttpMethod.Get)
            .pipe(map((json: any) => new NotificationReportData(json)));
    }

    getPhotosReport(startDate: Date, endDate: Date): Observable<PhotoUploadReportData>
    {
        return this.apiRequest(this.getPhotosReportUrl(startDate, endDate), HttpMethod.Get)
            .pipe(map((json: any) => new PhotoUploadReportData(json)));
    }

    getCheckInCheckOutReport(startDate: Date, endDate: Date): Observable<CheckInCheckOutReportData>
    {
        return this.apiRequest(this.getCheckInCheckOutReportUrl(startDate, endDate), HttpMethod.Get)
            .pipe(map((json: any) => new CheckInCheckOutReportData(json)));
    }

    getUserLoginCsv(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getUserLoginCsvUrl(startDate, endDate));
    }

    getRegisteredUserCsv(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getRegisteredUserCsvUrl(startDate, endDate));
    }

    getClientListCsv(): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getClientListCsvUrl);
    }

    getCheckInCheckOutCsv(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getCheckInCheckOutCsvUrl(startDate, endDate));
    }

    getEventListCsv(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getEventListCsvUrl(startDate, endDate));
    }

    getMultiOrgChildrenListCsv(): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getMultiOrgChildrenListCsvUrl);
    }

    getMultiOrgActivityListCsv(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getMultiOrgActivityListCsvUrl(startDate, endDate));
    }

    getMultiOrgRegistrationListCsv(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getMultiOrgRegistrationListCsvUrl(startDate, endDate));
    }

    emailPINReport(): Observable<Response>
    {
        return this.apiRequest(this.emailPINReportUrl, HttpMethod.Get);
    }

    getFaceRecognitionByChildReport(startDate: Date, endDate: Date): Observable<Response>
    {
        return this.apiRequest(this.getFaceRecognitionByChildReportUrl(startDate, endDate), HttpMethod.Get);
    }

	getGlobalAdminReport(startDate: Date, endDate: Date): Observable<GlobalAdminReportData>
	{
		return this.apiRequest(this.getGlobalAdminReportUrl(startDate, endDate), HttpMethod.Get).pipe(map((json: any) => new GlobalAdminReportData(json)));
	}

	// =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================
    /* tslint:disable:member-ordering */
    private get baseReportsApiUrl(): string { return `${this.baseApiUrl}reports`; }
    getUserLoginReportUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/userLoginReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getRegisteredUserReportUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/registeredUserReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getUserActionReportUrl(startDate: Date, endDate: Date, actionType: UserActionType, additionalDataId: number)
    {
        return `${this.baseReportsApiUrl}/userActionReport?actionType=${actionType}&additionalDataId=${additionalDataId === 0 ? '' : additionalDataId}` +
            `&${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getNotificationReportUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/notificationReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getPhotosReportUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/photosReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getCheckInCheckOutReportUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/CheckInCheckOutReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getUserLoginCsvUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/userLoginCsv?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getRegisteredUserCsvUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/registeredUserCsv?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    get getClientListCsvUrl(): string { return `${this.baseReportsApiUrl}/clientList?`; }
    getCheckInCheckOutCsvUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/checkInCheckOutCsv?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getEventListCsvUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/eventList?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    get getMultiOrgChildrenListCsvUrl(): string { return `${this.baseReportsApiUrl}/multiOrgChildrenList`; }
    getMultiOrgActivityListCsvUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/multiOrgActivityList?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    getMultiOrgRegistrationListCsvUrl(startDate: Date, endDate: Date)
    {
        return `${this.baseReportsApiUrl}/multiOrgRegistrationList?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
    get emailPINReportUrl(): string { return `${this.baseReportsApiUrl}/pinReport`; }
    getFaceRecognitionByChildReportUrl(startDate: Date, endDate: Date): string
    {
        return `${this.baseReportsApiUrl}/faceRecognitionByChildReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
    }
	getGlobalAdminReportUrl(startDate: Date, endDate: Date)
	{
		return `${this.baseReportsApiUrl}/globalAdminReport?${this.getStartAndEndDateParams(startDate, endDate)}`;
	}

    private getStartAndEndDateParams(startDate: Date, endDate: Date): string {
        return `startDate=${startDate == null ? '' : moment(startDate).format('YYYY-MM-DD')}` +
            `&endDate=${endDate == null ? '' : moment(endDate).format('YYYY-MM-DD')}`;
    }
}
